export const SITE_NAME = "BytaMed";
export const SITE_URL = "https://bytamed.com";
export const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
export const DEFAULT_TITLE = `${SITE_NAME} | Healthcare connections, listings, advice and community`;
export const DEFAULT_DESCRIPTION =
  "BytaMed helps people trade and share things, skills, and experiences locally while building trusted community connections.";

export const PRIVATE_ROUTE_PATTERNS = [
  /^\/dashboard\/?$/,
  /^\/profile(?:\/|$)/,
  /^\/my-listings\/?$/,
  /^\/messages\/?$/,
  /^\/upload\/(material|pet|hangout)\/?$/,
  /^\/admin\/?$/,
  /^\/auth\/callback\/?$/,
  /^\/reset-password\/?$/,
];

export const PUBLIC_PRERENDER_ROUTES = [
  "/",
  "/categories",
  "/materials",
  "/pets",
  "/hangouts",
  "/advice",
  "/adviceanswers",
  "/profiles",
  "/search",
  "/contact",
  "/privacy",
  "/terms",
  "/login",
  "/forgot-password",
  "/verify-email",
];

export const routeSeo = [
  {
    match: /^\/$/,
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonical: `${SITE_URL}/`,
    type: "WebSite",
    priority: 1.0,
  },
  {
    match: /^\/categories\/?$/,
    title: `Categories | ${SITE_NAME}`,
    description: "Explore local exchanges on BytaMed including things, skills, experiences, and community advice.",
    canonical: `${SITE_URL}/categories`,
    type: "CollectionPage",
  },
  {
    match: /^\/materials\/?$/,
    title: `Materials | ${SITE_NAME}`,
    description: "Browse useful materials on BytaMed, discover local listings, and connect with people nearby.",
    canonical: `${SITE_URL}/materials`,
    type: "CollectionPage",
  },
  {
    match: /^\/materials\/[^/]+\/?$/,
    title: `Material details | ${SITE_NAME}`,
    description: "View material details on BytaMed and explore trusted local exchange listings.",
    type: "Product",
  },
  {
    match: /^\/pets\/?$/,
    title: `Pets | ${SITE_NAME}`,
    description: "Explore pet-related community listings on BytaMed and connect with trusted people nearby.",
    canonical: `${SITE_URL}/pets`,
    type: "CollectionPage",
  },
  {
    match: /^\/pets\/[^/]+\/?$/,
    title: `Pet details | ${SITE_NAME}`,
    description: "View pet listing details on BytaMed and connect with your community through trusted discovery tools.",
    type: "Product",
  },
  {
    match: /^\/hangouts\/?$/,
    title: `Hangouts | ${SITE_NAME}`,
    description: "Find local hangouts and community meetups on BytaMed to create trusted real-world connections.",
    canonical: `${SITE_URL}/hangouts`,
    type: "CollectionPage",
  },
  {
    match: /^\/hangouts\/[^/]+\/?$/,
    title: `Hangout details | ${SITE_NAME}`,
    description: "View hangout details on BytaMed and discover trusted local opportunities to connect.",
    type: "Event",
  },
  {
    match: /^\/advice\/?$/,
    title: `Advice | ${SITE_NAME}`,
    description: "Browse advice topics on BytaMed and discover helpful community insights, skills, and shared experiences.",
    canonical: `${SITE_URL}/advice`,
    type: "CollectionPage",
  },
  {
    match: /^\/adviceanswers\/?$/,
    title: `Advice answers | ${SITE_NAME}`,
    description: "Read community advice answers on BytaMed and explore practical guidance from trusted people.",
    canonical: `${SITE_URL}/adviceanswers`,
    type: "CollectionPage",
  },
  {
    match: /^\/profiles\/?$/,
    title: `Profiles | ${SITE_NAME}`,
    description: "Discover profiles on BytaMed and connect with trusted people in your area.",
    canonical: `${SITE_URL}/profiles`,
    type: "CollectionPage",
  },
  {
    match: /^\/profiles\/[^/]+\/?$/,
    title: `Profile details | ${SITE_NAME}`,
    description: "View profile details on BytaMed and learn more about trusted community members.",
    type: "ProfilePage",
  },
  {
    match: /^\/search\/?$/,
    title: `Search | ${SITE_NAME}`,
    description: "Search things, skills, experiences, and profiles on BytaMed to find local opportunities faster.",
    canonical: `${SITE_URL}/search`,
    type: "SearchResultsPage",
  },
  {
    match: /^\/contact\/?$/,
    title: `Contact | ${SITE_NAME}`,
    description: "Contact BytaMed for questions, support, or partnership inquiries.",
    canonical: `${SITE_URL}/contact`,
    type: "ContactPage",
  },
  {
    match: /^\/privacy\/?$/,
    title: `Privacy Policy | ${SITE_NAME}`,
    description: "Read the BytaMed privacy policy and learn how user data is handled across the platform.",
    canonical: `${SITE_URL}/privacy`,
    type: "WebPage",
  },
  {
    match: /^\/terms\/?$/,
    title: `Terms of Service | ${SITE_NAME}`,
    description: "Read the BytaMed terms of service for platform usage, responsibilities, and policies.",
    canonical: `${SITE_URL}/terms`,
    type: "WebPage",
  },
  {
    match: /^\/login\/?$/,
    title: `Login | ${SITE_NAME}`,
    description: "Log in to your BytaMed account to manage your profile, messages, listings, and community activity.",
    canonical: `${SITE_URL}/login`,
    type: "WebPage",
  },
  {
    match: /^\/forgot-password\/?$/,
    title: `Forgot password | ${SITE_NAME}`,
    description: "Reset access to your BytaMed account securely.",
    canonical: `${SITE_URL}/forgot-password`,
    type: "WebPage",
  },
  {
    match: /^\/reset-password\/?$/,
    title: `Reset password | ${SITE_NAME}`,
    description: "Create a new password for your BytaMed account securely.",
    canonical: `${SITE_URL}/reset-password`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/verify-email\/?$/,
    title: `Verify email | ${SITE_NAME}`,
    description: "Verify your BytaMed email address to secure your account and unlock trusted features.",
    canonical: `${SITE_URL}/verify-email`,
    type: "WebPage",
  },
  {
    match: /^\/dashboard\/?$/,
    title: `Dashboard | ${SITE_NAME}`,
    description: "Manage your BytaMed activity, listings, and account from your personal dashboard.",
    canonical: `${SITE_URL}/dashboard`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/profile\/?$/,
    title: `My profile | ${SITE_NAME}`,
    description: "Manage your BytaMed profile, reputation, and account settings.",
    canonical: `${SITE_URL}/profile`,
    type: "ProfilePage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/profile\/edit\/?$/,
    title: `Edit profile | ${SITE_NAME}`,
    description: "Update your BytaMed profile information, trust details, and account settings.",
    canonical: `${SITE_URL}/profile/edit`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/my-listings\/?$/,
    title: `My listings | ${SITE_NAME}`,
    description: "Manage your published listings on BytaMed.",
    canonical: `${SITE_URL}/my-listings`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/messages\/?$/,
    title: `Messages | ${SITE_NAME}`,
    description: "Read and manage your BytaMed conversations and community messages.",
    canonical: `${SITE_URL}/messages`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/upload\/material\/?$/,
    title: `Upload material | ${SITE_NAME}`,
    description: "Create a new materials listing on BytaMed.",
    canonical: `${SITE_URL}/upload/material`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/upload\/pet\/?$/,
    title: `Upload pet | ${SITE_NAME}`,
    description: "Create a new pet listing on BytaMed.",
    canonical: `${SITE_URL}/upload/pet`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/upload\/hangout\/?$/,
    title: `Upload hangout | ${SITE_NAME}`,
    description: "Create a new hangout listing on BytaMed.",
    canonical: `${SITE_URL}/upload/hangout`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/admin\/?$/,
    title: `Admin dashboard | ${SITE_NAME}`,
    description: "Manage moderation and administration tools on BytaMed.",
    canonical: `${SITE_URL}/admin`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
  {
    match: /^\/auth\/callback\/?$/,
    title: `Sign-in callback | ${SITE_NAME}`,
    description: "Complete secure sign-in to BytaMed.",
    canonical: `${SITE_URL}/auth/callback`,
    type: "WebPage",
    robots: "noindex, nofollow",
  },
];

export function titleFromSlug(pathname = "/") {
  const slug = pathname.split("/").filter(Boolean).pop() || "listing";
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export function normalizeCanonicalPath(pathname = "/") {
  if (!pathname || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function isPrivateRoute(pathname = "/") {
  return PRIVATE_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
}

export function getSeoForPath(pathname = "/") {
  return (
    routeSeo.find((entry) => entry.match.test(pathname)) || {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      canonical: `${SITE_URL}${normalizeCanonicalPath(pathname)}`,
      type: "WebPage",
      robots: isPrivateRoute(pathname) ? "noindex, nofollow" : "index, follow",
    }
  );
}
